import { ChartDataType } from "../chart/types.js";
import { guardNever } from "../errors.js";
import { CalciteType, HqlAggregationFunction } from "../hql/types.js";
import { SemanticDatasetName } from "../idTypeBrands.js";
import { uuid } from "../uuid.js";

import {
  ExploreChannel,
  ExploreChartSeries,
  ExploreField,
  ExploreFieldId,
  ExploreFieldType,
  ExploreSeriesId,
  ExploreSort,
  ExploreSpec,
  ExploreTimeUnit,
  ExploreVisualizationType,
} from "./types.js";

import { ExploreInputField, getDefaultViewTypeForVizType } from "./index.js";
export function defaultExploreSpec({
  noSeries,
  visualizationType,
}: {
  noSeries?: boolean;
  visualizationType?: ExploreVisualizationType;
} = {}): ExploreSpec {
  const initialSeries = defaultExploreChartSeries();
  return {
    fields: [],
    details: {
      fields: [],
      enabled: true,
      showAllBaseTableDetailFields: true,
    },
    rowTotals: true,
    columnTotals: true,
    visualizationType: visualizationType ?? "chart",
    viewType: getDefaultViewTypeForVizType(visualizationType ?? "chart"),
    chartConfig: {
      orientation: "vertical",
      series: noSeries ? [] : [initialSeries],
      seriesGroups: noSeries ? [] : [[initialSeries.id]],
      settings: {
        legend: {
          position: "right",
        },
      },
    },
  };
}

export function defaultPivotTableExploreSpec(): ExploreSpec {
  return {
    fields: [],
    details: {
      fields: [],
      enabled: true,
      showAllBaseTableDetailFields: true,
    },
    visualizationType: "pivot-table",
    viewType: "both",
    // This chart config is just here to conform to the ExploreSpec type;
    // it's not used for pivot tables.
    chartConfig: {
      orientation: "vertical",
      series: [],
      seriesGroups: [],
      settings: {
        legend: {
          position: "right",
        },
      },
    },
  };
}

export function defaultExploreChartSeries(
  type: "bar" | "line" = "bar",
): ExploreChartSeries {
  return { id: ExploreSeriesId.check(uuid()), type };
}

export const defaultExploreAggregation = (
  channel: ExploreChannel,
  field: Pick<ExploreInputField, "type" | "fieldType" | "aggregation">,
): ExploreField["aggregation"] => {
  if (field.fieldType === ExploreFieldType.MEASURE) {
    // measures have their own aggregation definition, so no aggregation is set
    // on the explore field
    return undefined;
  }
  if (field.aggregation) return field.aggregation;
  const isNumeric = field.type === "NUMBER";
  switch (channel) {
    case "cross-axis":
    case "opacity":
      return isNumeric ? "Sum" : "CountDistinct";

    case "tooltip":
    case "color":
      return isNumeric ? "Sum" : undefined;

    case "base-axis":
    case "h-facet":
    case "v-facet":
      return undefined;

    case "row":
    case "column":
      return undefined;
    case "value":
      return isNumeric ? "Sum" : "CountDistinct";

    case "source":
    case "destination":
      return undefined;

    default:
      guardNever(channel, channel);
      return undefined;
  }
};

export function defaultExploreField({
  aggregation,
  channel,
  dataType,
  displayFormat,
  fieldType,
  queryPath,
  scaleType,
  seriesId,
  sort,
  title,
  truncUnit,
  value,
}: {
  channel: ExploreChannel;
  value: string;
  seriesId: ExploreSeriesId;
  dataType: CalciteType;
  scaleType?: ChartDataType;
  displayFormat?: ExploreField["displayFormat"];
  aggregation?: HqlAggregationFunction;
  truncUnit?: ExploreTimeUnit;
  fieldType?: ExploreFieldType;
  queryPath?: SemanticDatasetName[];
  sort?: ExploreSort;
  title?: string;
}): ExploreField {
  return {
    id: ExploreFieldId.check(uuid()),
    channel: channel,
    seriesId: seriesId,
    value: value,
    dataType,
    scaleType,
    truncUnit,
    displayFormat,
    aggregation,
    fieldType: fieldType ?? ExploreFieldType.COLUMN,
    queryPath: queryPath ?? [],
    sort,
    title,
  };
}
